import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, scan, startWith, takeWhile, timer} from 'rxjs';
import {filter, finalize, tap} from 'rxjs/operators';
import {add, differenceInSeconds, getTime, isAfter} from 'date-fns';
import {OtpDeliveryMethod} from '../../auth/login/login.model';
import {Timer} from './timer.model';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TimerService {
  private _smsRemainingTime$ = new BehaviorSubject<number>(undefined);
  readonly smsRemainingTime$ = this._smsRemainingTime$.asObservable();
  get smsRemainingTime(): number { return this._smsRemainingTime$.getValue(); }

  private _emailRemainingTime$ = new BehaviorSubject<number>(undefined);
  readonly emailRemainingTime$ = this._emailRemainingTime$.asObservable();
  get emailRemainingTime(): number { return this._emailRemainingTime$.getValue(); }

  timerObject = {sms: null, email: null} as Timer;
  timer = 60;
  maxSeconds = 60;
  timestamp = 60;
  otpSended = false;
  interval: any;
  timeout: any;

  setIntervalForNewOTP(): any {
    return setInterval(() => {
      this.timer = this.maxSeconds - Math.floor((Date.now() - this.timestamp) / 1000);
    }, 1000);
  }

  resetTimerSendOTP(): void {
    this.timer = this.maxSeconds;
    this.otpSended = false;
    clearInterval(this.interval);
    clearTimeout(this.timeout);
    localStorage.removeItem('timestampSendedOTP');
  }

  setTimerOTP(timestampSendedOTP: number = Date.now(), timeElapsedInSecs: number = 0): void {
    this.timestamp = timestampSendedOTP;
    this.interval = this.setIntervalForNewOTP();
    localStorage.setItem('timestampSendedOTP', String(this.timestamp));
    this.timeout = setTimeout(() => {
      this.resetTimerSendOTP();
      clearInterval(this.interval);
    }, (this.maxSeconds - timeElapsedInSecs) * 1000);
    this.otpSended = true;
  }

  isTimerSendOTPSetted(sendOTPOnOpen: boolean = false): boolean {
    const timestampSendedOTP = +localStorage.getItem('timestampSendedOTP');
    if (!timestampSendedOTP) {
      if (sendOTPOnOpen) {
        this.setTimerOTP();
      }
      return true;
    }
    const timestampNow = Date.now();
    const timeElapsedInSecs = Math.floor((timestampNow - timestampSendedOTP) / 1000);
    if (timeElapsedInSecs < this.maxSeconds) {
      this.setTimerOTP(timestampSendedOTP, timeElapsedInSecs);
      if (!sendOTPOnOpen) {
        return true;
      }
      return false;
    }
    localStorage.removeItem('timestampSendedOTP');
    return false;
  }

  private getTimestampEndTimerName(otpDeliveryMethod: OtpDeliveryMethod): string {
    return otpDeliveryMethod + 'TimestampEndTimer';
  }

  private getBsToUpdate(otpDeliveryMethod: OtpDeliveryMethod): BehaviorSubject<number> {
    switch (otpDeliveryMethod) {
      case OtpDeliveryMethod.SMS:
        return this._smsRemainingTime$;
      case OtpDeliveryMethod.EMAIL:
        return this._emailRemainingTime$;
      default:
        return this._smsRemainingTime$;
    }
  }

  getObservable(otpDeliveryMethod: OtpDeliveryMethod): Observable<number> {
    switch (otpDeliveryMethod) {
      case OtpDeliveryMethod.SMS:
        return this.smsRemainingTime$;
      case OtpDeliveryMethod.EMAIL:
        return this.emailRemainingTime$;
      default:
        return null;
    }
  }

  getMaxWaitTime(otpDeliveryMethod: OtpDeliveryMethod): number {
    switch (otpDeliveryMethod) {
      case OtpDeliveryMethod.SMS:
        return environment.newOtpOnSmsWaitTime;
      case OtpDeliveryMethod.EMAIL:
        return environment.newOtpOnEmailWaitTime;
      default:
        return 60;
    }
  }

  private saveTimestamp(now: number, seconds: number, localStorageName: string): number {
    const timestampEndTimer = getTime(add(now, {seconds}));
    localStorage.setItem(localStorageName, String(timestampEndTimer));
    return timestampEndTimer;
  }

  createTimer(otpDeliveryMethod: OtpDeliveryMethod): void {
    if (otpDeliveryMethod === OtpDeliveryMethod.APP) {
      return;
    }
    const now = Date.now();
    const localStorageName = this.getTimestampEndTimerName(otpDeliveryMethod);
    let timestampEndTimer: number;
    if (!localStorage.getItem(localStorageName)) {
      timestampEndTimer = this.saveTimestamp(now, this.getMaxWaitTime(otpDeliveryMethod), localStorageName);
    } else {
      timestampEndTimer = +localStorage.getItem(localStorageName);
    }
    let currentTimer = this.timerObject[otpDeliveryMethod as keyof Timer];
    const _bsToUpdate$ = this.getBsToUpdate(otpDeliveryMethod);
    if (isAfter(now, timestampEndTimer)) {
      currentTimer?.unsubscribe();
      this.timerObject[otpDeliveryMethod as keyof Timer] = null;
      timestampEndTimer = this.saveTimestamp(now, this.getMaxWaitTime(otpDeliveryMethod), localStorageName);
    }
    const secondsUntilEndTimer = differenceInSeconds(timestampEndTimer, now);
    currentTimer = timer(0, 1000).pipe(
      startWith(secondsUntilEndTimer),
      scan((acc, curr) => !acc ? secondsUntilEndTimer : acc - 1),
      tap(value => _bsToUpdate$.next(value)),
      takeWhile(value => value > 0),
      filter(value => value > 0),
      finalize(() => {
        localStorage.removeItem(localStorageName);
        this.timerObject[otpDeliveryMethod as keyof Timer] = null;
      })
    ).subscribe();
    this.timerObject[otpDeliveryMethod as keyof  Timer] = currentTimer;
  }
}
